// Vencimientos (GET /documents/expiring?days=): documentos que vencen en la
// ventana elegida y los ya vencidos sin reemplazo, agrupados por urgencia.
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button, Spinner, cn } from "../../../components/ui";
import { LoadBlock, Toolbar } from "../../bases-propiedades/components/console";
import { documentTypeName } from "../../../utils/documentTypes";
import { documentsErrorMessage, type ShipmentDocument } from "../../../services/shipmentDocuments";
import { DOCS_NS, shortDate } from "../../../features/shipmentDocuments/meta";
import { ExpiryChip, StatusChip } from "../../../features/shipmentDocuments/DocumentChecklist";
import { openDownload } from "../../../features/shipmentDocuments/hooks";
import { useCompanyExpiring } from "../queries";
import { SubjectCell, queryStatus } from "../components/pieces";

const WINDOWS = [7, 15, 30, 60, 90, 180];

export default function VencimientosSection() {
  const { t, i18n } = useTranslation(DOCS_NS);
  const [days, setDays] = useState(30);
  const query = useCompanyExpiring(days);
  const qs = queryStatus(query);
  const [downloading, setDownloading] = useState<string | null>(null);

  const groups = useMemo(() => {
    const rows = query.data ?? [];
    const g: Array<{ id: string; title: string; tone: "error" | "warning" | "neutral"; rows: ShipmentDocument[] }> = [
      { id: "expired", title: t("vencimientos.expired"), tone: "error", rows: rows.filter((d) => (d.expires_in_days ?? 1) <= 0) },
      { id: "soon", title: t("vencimientos.soon"), tone: "warning", rows: rows.filter((d) => (d.expires_in_days ?? 99) > 0 && (d.expires_in_days ?? 99) <= 7) },
      { id: "later", title: t("vencimientos.later"), tone: "neutral", rows: rows.filter((d) => (d.expires_in_days ?? 0) > 7) },
    ];
    return g.filter((x) => x.rows.length > 0);
  }, [query.data, t]);

  const download = async (id: string) => {
    setDownloading(id);
    try {
      await openDownload(id);
    } catch (err) {
      toast.error(documentsErrorMessage(err, t("checklist.download.failed")));
    } finally {
      setDownloading(null);
    }
  };

  const total = query.data?.length ?? 0;

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <Toolbar>
        <div className="flex items-center gap-2">
          <i className="ri-calendar-close-line text-lg text-primary-500" />
          <h1 className="text-sm font-semibold text-content">{t("vencimientos.title")}</h1>
          <span className="text-xs text-content-muted">{t("vencimientos.count", { n: total })}</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-content-secondary">
            {t("vencimientos.window")}
            <select aria-label={t("vencimientos.window")} value={days} onChange={(e) => setDays(Number(e.target.value))} className="rounded-lg border border-line bg-app px-2 py-1.5 text-sm text-content outline-none focus:border-primary-500">
              {WINDOWS.map((w) => (
                <option key={w} value={w}>
                  {t("vencimientos.daysN", { n: w })}
                </option>
              ))}
            </select>
          </label>
          <Button size="sm" variant="ghost" icon={<i className="ri-refresh-line" />} onClick={() => void query.refetch()}>
            {t("common.refresh")}
          </Button>
        </div>
      </Toolbar>
      <div className="flex-1 overflow-y-auto p-3">
        <LoadBlock status={qs.status} error={qs.error} onRetry={qs.retry} empty={total === 0} emptyIcon="ri-calendar-check-line" emptyTitle={t("vencimientos.empty")} emptyText={t("vencimientos.emptyText")}>
          <div className="space-y-4" data-testid="expiring-groups">
            {groups.map((g) => (
              <section key={g.id} className="overflow-hidden rounded-xl border border-line bg-card" aria-labelledby={`exp-${g.id}`}>
                <h2 id={`exp-${g.id}`} className={cn("flex items-center gap-2 border-b border-line px-3 py-2 text-xs font-semibold uppercase tracking-wide", g.tone === "error" ? "text-error" : g.tone === "warning" ? "text-warning" : "text-content-secondary")}>
                  <i className={g.tone === "error" ? "ri-alarm-warning-line" : g.tone === "warning" ? "ri-time-line" : "ri-calendar-line"} /> {g.title}
                  <span className="ml-auto tabular-nums text-content-muted">{g.rows.length}</span>
                </h2>
                <table className="w-full text-sm">
                  <thead className="sr-only">
                    <tr>
                      <th>{t("vencimientos.columns.document")}</th>
                      <th>{t("vencimientos.columns.subject")}</th>
                      <th>{t("vencimientos.columns.expires")}</th>
                      <th>{t("vencimientos.columns.inDays")}</th>
                      <th>{t("vencimientos.columns.status")}</th>
                      <th>{t("common.actions")}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line">
                    {g.rows.map((d) => (
                      <tr key={d.id} className="hover:bg-subtle">
                        <td className="max-w-[16rem] px-3 py-2">
                          <div className="truncate font-medium text-content">{documentTypeName(d.doc_type, i18n.language, d.name)}</div>
                          <div className="truncate text-[11px] text-content-muted">{d.reference || d.versions[d.versions.length - 1]?.file_name || "—"}</div>
                        </td>
                        <td className="max-w-[14rem] px-3 py-2">
                          <SubjectCell subject={d.subject} label={d.subject.id} secondary={t(`kind.${d.subject.kind}`)} />
                        </td>
                        <td className="hidden px-3 py-2 text-xs text-content-secondary md:table-cell">{shortDate(d.expires_at, i18n.language)}</td>
                        <td className="px-3 py-2">
                          <ExpiryChip days={d.expires_in_days} expiresAt={d.expires_at} />
                        </td>
                        <td className="hidden px-3 py-2 md:table-cell">
                          <StatusChip status={d.effective_status} />
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button type="button" disabled={!d.has_file || downloading === d.id} onClick={() => void download(d.id)} aria-label={t("common.download")} title={t("common.download")} className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-content-secondary hover:bg-subtle hover:text-content disabled:opacity-40">
                            {downloading === d.id ? <Spinner size="sm" /> : <i className="ri-download-2-line" />}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            ))}
          </div>
        </LoadBlock>
      </div>
    </div>
  );
}
